import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Spin, Typography } from 'antd';
import { notificationService } from '../services/api';

const { Title, Text } = Typography;

interface NotificationItem {
  _id: string;
  title: string;
  body: string;
  read: boolean;
  createdAt?: string;
}

const NotificationHistory: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const unreadNotifications = useMemo(
    () => notifications.filter((notification) => !notification.read),
    [notifications]
  );

  const loadAllNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationService.list({ all: true });
      if (response.data?.success) {
        setNotifications(response.data.data?.notifications || []);
        setUnreadCount(response.data.data?.unreadCount || 0);
      }
    } catch (error) {
      console.error('Failed to load notifications', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllNotifications();
  }, [loadAllNotifications]);

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((previousNotifications) =>
        previousNotifications.map((notification) =>
          notification._id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
      setUnreadCount((previousCount) => Math.max(previousCount - 1, 0));
    } catch (error) {
      console.error('Failed to mark notification as read', error);
    }
  };

  const markAllAsRead = async () => {
    if (!unreadNotifications.length) return;

    try {
      await Promise.all(
        unreadNotifications.map((notification) =>
          notificationService.markAsRead(notification._id)
        )
      );
      setNotifications((previousNotifications) =>
        previousNotifications.map((notification) => ({ ...notification, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title level={2} className="!mb-1">
            All Notifications
          </Title>
          <Text type="secondary">
            Total: {notifications.length} | Unread: {unreadCount}
          </Text>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={loadAllNotifications}>Refresh</Button>
          <Button type="primary" onClick={markAllAsRead} disabled={!unreadNotifications.length}>
            Mark All Read
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center">
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-10">
            <Empty description="No notifications found" />
          </div>
        ) : (
          <div>
            {notifications.map((notification) => (
              <div
                key={notification._id}
                className={`p-4 border-b border-gray-100 ${
                  notification.read ? 'bg-white' : 'bg-blue-50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900">{notification.title}</div>
                    <div className="text-sm text-gray-600 mt-1">{notification.body}</div>
                    <div className="text-xs text-gray-400 mt-2">
                      {notification.createdAt
                        ? new Date(notification.createdAt).toLocaleString()
                        : ''}
                    </div>
                  </div>
                  {!notification.read && (
                    <Button size="small" onClick={() => markAsRead(notification._id)}>
                      Mark Read
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationHistory;
